import { APP_CONFIG, AppConfig } from '../../../config/app.config';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Inject,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  FilterPillsSkeletonComponent
} from '../../../components/ui/filter-pills/filter-pills-skeleton/filter-pills-skeleton.component';
import { FilterPillsComponent } from '../../../components/ui/filter-pills/filter-pills.component';
import {RouterRequestService} from '../../../services/router/requests/avia-request.service';
import {
  AviaSearchAppliedFilter,
  AviaSearchAppliedFilterGroup,
  AviaSearchAppliedFilters,
  AviaSearchAppliedFiltersRequest,
  AviaSearchFilterItem,
  AviaSearchFilters,
  AviaSearchFiltersResponse,
  AviaSearchPriceSelected,
  AviaSearchSortBy,
  AviaSearchTimeSelected,
  AviaSearchStatus
} from '../../../services/router/interfaces/avia-search';
import {Subscription} from 'rxjs';
import {WindowRefService} from '../../../services/window-ref.service';

@Component({
  selector: 'app-avia-filters',
  templateUrl: './avia-filters.component.html',
  styleUrl: './avia-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FilterPillsSkeletonComponent,
    FilterPillsComponent
  ]
})
export class AviaFiltersComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('filtersWrapper') private filtersWrapperRef?: ElementRef<HTMLElement>;
  @ViewChild('filtersBar') private filtersBarRef?: ElementRef<HTMLElement>;

  @ViewChild(FilterPillsComponent) private filterPills?: FilterPillsComponent;
  @Input() searchId?: string;
  @Input() initialFiltersRequest: AviaSearchAppliedFiltersRequest | null = null;
  @Output() applyFilters = new EventEmitter<AviaSearchAppliedFiltersRequest>();

  private readonly skeletonShowDelay = 1500;
  private readonly inProgressReloadDelay = 5000;
  private readonly defaultSortBy: AviaSearchSortBy = 'price';
  private readonly sortByFilter: AviaSearchFilterItem = {
    filterKey: 'sortBy',
    name: 'Сортировка',
    type: 'select',
    selected: this.defaultSortBy,
    values: [
      {
        filterValue: 'price',
        viewValue: 'Сначала дешевые',
        isNoop: true,
      },
      {
        filterValue: 'duration',
        viewValue: 'Сначала быстрые',
      },
      {
        filterValue: 'departure_time_asc',
        viewValue: 'Вылет раньше',
      },
      {
        filterValue: 'departure_time_desc',
        viewValue: 'Вылет позже',
      },
    ],
  };

  isShowSkeletons = true;
  hasFilters = false;
  filteredTotal = 0;
  filtersStatus: AviaSearchStatus | null = null;
  isFiltersFixed = false;
  filtersHeight = 0;

  filters: AviaSearchFilters = null;
  filtersWithSort: AviaSearchFilters = [this.sortByFilter];
  appliedFilters: AviaSearchAppliedFilter[] = [];

  private loadedSearchId = '';
  private filtersSubscription: Subscription = null;
  private filtersReloadTimeout: any = null;
  private skeletonDelayTimeout: any = null;
  private fixedFiltersFrameId: number | null = null;
  private isSkeletonDelayPassed = false;

  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    private requestService: RouterRequestService,
    private cdRef: ChangeDetectorRef,
    private windowRef: WindowRefService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.searchId || !this.searchId || this.searchId === this.loadedSearchId) {
      return;
    }

    this.loadFilters(this.searchId);
  }

  filtersLoadFailed = false;
  private openFiltersAfterLoad = false;

  get resetFiltersLabel(): string {
    return this.config.translates.aviaListing.resetFilters;
  }

  resetUnavailableFilters() {
    this.appliedFilters = [];
    this.emitApplyFilters();
    this.showAllFilters();
  }

  showAllFilters() {
    if (this.filterPills) {
      this.filterPills.showFilters();
    } else if (this.searchId) {
      this.openFiltersAfterLoad = true;
      this.requestFilters(this.searchId);
    }
  }

  ngAfterViewInit() {
    this.scheduleFixedFiltersRefresh();
  }

  ngOnDestroy() {
    this.clearFiltersReloadTimeout();
    this.clearSkeletonDelayTimeout();
    this.clearFixedFiltersFrame();

    if (this.filtersSubscription) {
      this.filtersSubscription.unsubscribe();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.refreshFixedFiltersState();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.scheduleFixedFiltersRefresh();
  }

  private loadFilters(searchId: string) {
    if (this.filtersSubscription) {
      this.filtersSubscription.unsubscribe();
    }

    this.clearFiltersReloadTimeout();
    this.clearSkeletonDelayTimeout();
    this.isSkeletonDelayPassed = false;
    this.isShowSkeletons = true;
    this.hasFilters = false;
    this.filters = [];
    this.filtersWithSort = [this.sortByFilter];
    this.appliedFilters = Object.entries(this.initialFiltersRequest?.filters || {})
      .map(([filterKey, selected]) => ({ filterKey, selected }));
    if (this.initialFiltersRequest?.sortBy) {
      this.appliedFilters.push({ filterKey: 'sortBy', selected: this.initialFiltersRequest.sortBy });
    }
    this.isFiltersFixed = false;
    this.filtersHeight = 0;
    this.loadedSearchId = searchId;
    this.cdRef.markForCheck();

    this.scheduleSkeletonDelay(searchId);
    this.requestFilters(searchId);
  }

  private requestFilters(searchId: string) {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = this.requestService.getAviaFilters(searchId)
      .subscribe({
        next: (response) => this.applyFiltersResponse(response),
        error: () => {
          this.resetFilters();
          this.filtersLoadFailed = true;
          this.cdRef.markForCheck();
        },
      });
  }

  private applyFiltersResponse(response: AviaSearchFiltersResponse) {
    const labels = this.config.translates.aviaListing;
    const responseFilters = (response?.filters || []).map((filter) => filter.filterKey === 'singleAirline'
      ? {
        ...filter,
        name: labels.sameAirline,
        values: [
          { filterValue: 'true', viewValue: labels.yes },
          { filterValue: 'false', viewValue: labels.no, isNoop: true },
        ],
      }
      : filter);

    this.filtersLoadFailed = false;
    this.filtersStatus = response?.status || null;
    this.filteredTotal = response?.filteredTotal || 0;
    this.filters = responseFilters;
    this.filtersWithSort = [
      this.sortByFilter,
      ...responseFilters,
    ];
    this.updateFiltersViewState();

    this.cdRef.markForCheck();
    this.scheduleFixedFiltersRefresh();

    if (response?.status === 'in_progress' && this.searchId === this.loadedSearchId) {
      this.scheduleFiltersReload(this.loadedSearchId);
    }
  }

  onApplyFilter(filter: AviaSearchAppliedFilter) {
    this.appliedFilters = [
      ...this.appliedFilters.filter((item) => item.filterKey !== filter.filterKey),
      filter,
    ];
    this.emitApplyFilters();
  }

  onApplyFilterGroup(filterGroup: AviaSearchAppliedFilterGroup) {
    this.appliedFilters = [
      ...this.appliedFilters.filter((item) => !filterGroup.filterKeys.includes(item.filterKey)),
      ...filterGroup.filters,
    ];
    this.emitApplyFilters();
  }

  onResetFilter(filterKey: string) {
    this.appliedFilters = this.appliedFilters.filter((filter) => filter.filterKey !== filterKey);
    this.emitApplyFilters();
  }

  onResetFilterGroup(filterKeys: string[]) {
    this.appliedFilters = this.appliedFilters.filter((filter) => !filterKeys.includes(filter.filterKey));
    this.emitApplyFilters();
  }

  private resetFilters() {
    this.clearFiltersReloadTimeout();
    this.clearSkeletonDelayTimeout();
    this.isSkeletonDelayPassed = true;
    this.filtersStatus = null;
    this.filteredTotal = 0;
    this.filters = [];
    this.filtersWithSort = [this.sortByFilter];
    this.hasFilters = false;
    this.isShowSkeletons = false;
  }

  private emitApplyFilters() {
    this.applyFilters.emit(this.buildAppliedFiltersRequest());
    this.cdRef.markForCheck();
  }

  private buildAppliedFiltersRequest(): AviaSearchAppliedFiltersRequest {
    const requestFilters: AviaSearchAppliedFilters = {};
    const priceFilter = this.appliedFilters.find((filter) => filter.filterKey === 'price');
    const airlinesFilter = this.appliedFilters.find((filter) => filter.filterKey === 'airlines');
    const flightTypeFilter = this.appliedFilters.find((filter) => filter.filterKey === 'flightType');
    const stopsFilter = this.appliedFilters.find((filter) => filter.filterKey === 'stops');
    const stopoverDurationFilter = this.appliedFilters.find((filter) => filter.filterKey === 'stopoverDuration');
    const withoutNightStopoversFilter = this.appliedFilters.find((filter) => filter.filterKey === 'withoutNightStopovers');
    const excludeLccFilter = this.appliedFilters.find((filter) => filter.filterKey === 'excludeLcc');
    const singleAirlineFilter = this.appliedFilters.find((filter) => filter.filterKey === 'singleAirline');
    const baggageOnlyFilter = this.appliedFilters.find((filter) => filter.filterKey === 'baggageOnly');
    const departureTimeFromOriginFilter = this.appliedFilters.find((filter) => filter.filterKey === 'departureTimeFromOrigin');
    const departureTimeFromDestinationFilter = this.appliedFilters.find((filter) => filter.filterKey === 'departureTimeFromDestination');
    const sortByFilter = this.appliedFilters.find((filter) => filter.filterKey === 'sortBy');

    if (priceFilter) {
      requestFilters.price = priceFilter.selected as AviaSearchPriceSelected;
    }

    if (airlinesFilter) {
      requestFilters.airlines = airlinesFilter.selected as string[];
    }

    if (flightTypeFilter) {
      requestFilters.flightType = flightTypeFilter.selected as string;
    }

    if (stopsFilter) {
      requestFilters.stops = stopsFilter.selected as string;
    }

    if (stopoverDurationFilter) {
      requestFilters.stopoverDuration = stopoverDurationFilter.selected as string;
    }

    if (withoutNightStopoversFilter) {
      requestFilters.withoutNightStopovers = withoutNightStopoversFilter.selected as string;
    }

    if (excludeLccFilter) {
      requestFilters.excludeLcc = excludeLccFilter.selected as string;
    }

    if (singleAirlineFilter) {
      requestFilters.singleAirline = singleAirlineFilter.selected as string;
    }

    if (baggageOnlyFilter) {
      requestFilters.baggageOnly = baggageOnlyFilter.selected as string;
    }

    if (departureTimeFromOriginFilter) {
      requestFilters.departureTimeFromOrigin = departureTimeFromOriginFilter.selected as AviaSearchTimeSelected;
    }

    if (departureTimeFromDestinationFilter) {
      requestFilters.departureTimeFromDestination = departureTimeFromDestinationFilter.selected as AviaSearchTimeSelected;
    }

    const request: AviaSearchAppliedFiltersRequest = {
      filters: requestFilters,
    };

    if (sortByFilter) {
      request.sortBy = sortByFilter.selected as AviaSearchSortBy;
    }

    return request;
  }

  private scheduleFiltersReload(searchId: string) {
    this.clearFiltersReloadTimeout();

    this.filtersReloadTimeout = setTimeout(() => {
      if (this.searchId === searchId) {
        this.requestFilters(searchId);
      }
    }, this.inProgressReloadDelay);
  }

  private clearFiltersReloadTimeout() {
    if (!this.filtersReloadTimeout) {
      return;
    }

    clearTimeout(this.filtersReloadTimeout);
    this.filtersReloadTimeout = null;
  }

  private updateFiltersViewState() {
    if (!this.isSkeletonDelayPassed) {
      this.isShowSkeletons = true;
      this.hasFilters = false;
      return;
    }

    if (this.filters.length > 0) {
      this.isShowSkeletons = false;
    } else {
      this.isShowSkeletons = this.filtersStatus === 'in_progress';
    }

    this.hasFilters = !this.isShowSkeletons && this.filters.length > 0;
    if (this.hasFilters && this.openFiltersAfterLoad) {
      this.openFiltersAfterLoad = false;
      this.cdRef.detectChanges();
      this.filterPills?.showFilters();
    }
  }

  private scheduleSkeletonDelay(searchId: string) {
    if (this.skeletonDelayTimeout) {
      return;
    }

    this.skeletonDelayTimeout = setTimeout(() => {
      this.skeletonDelayTimeout = null;

      if (this.searchId !== searchId || this.loadedSearchId !== searchId) {
        return;
      }

      this.isSkeletonDelayPassed = true;
      this.updateFiltersViewState();
      this.cdRef.markForCheck();
      this.scheduleFixedFiltersRefresh();
    }, this.skeletonShowDelay);
  }

  private clearSkeletonDelayTimeout() {
    if (!this.skeletonDelayTimeout) {
      return;
    }

    clearTimeout(this.skeletonDelayTimeout);
    this.skeletonDelayTimeout = null;
  }

  private scheduleFixedFiltersRefresh() {
    if (this.fixedFiltersFrameId !== null) {
      return;
    }

    this.fixedFiltersFrameId = this.windowRef.nativeWindow.requestAnimationFrame(() => {
      this.fixedFiltersFrameId = null;
      this.refreshFixedFiltersState();
    });
  }

  private refreshFixedFiltersState() {
    const wrapper = this.filtersWrapperRef?.nativeElement;
    const filtersBar = this.filtersBarRef?.nativeElement;

    if (!wrapper || !filtersBar) {
      return;
    }

    const scrollY = this.windowRef.nativeWindow.scrollY || 0;
    const wrapperTop = wrapper.getBoundingClientRect().top + scrollY;
    const nextFiltersHeight = filtersBar.offsetHeight;
    const nextIsFiltersFixed = scrollY >= wrapperTop;

    if (this.filtersHeight === nextFiltersHeight && this.isFiltersFixed === nextIsFiltersFixed) {
      return;
    }

    this.filtersHeight = nextFiltersHeight;
    this.isFiltersFixed = nextIsFiltersFixed;
    this.cdRef.markForCheck();
  }

  private clearFixedFiltersFrame() {
    if (this.fixedFiltersFrameId === null) {
      return;
    }

    this.windowRef.nativeWindow.cancelAnimationFrame(this.fixedFiltersFrameId);
    this.fixedFiltersFrameId = null;
  }
}
